/**
 * Approved clinic facts: treatments, personas, supervisors, CTA templates,
 * strengths and policies.
 *
 * One screen for all of them, driven by one field map, so a new column is added
 * in a single place rather than in six near-identical files. The map mirrors the
 * server's RESOURCES in modules/catalog/routes.ts - that is the contract, and
 * anything not listed there is rejected as an unknown property.
 *
 * This screen is where the material an article may cite comes from. It was
 * read-only until now, which made everything downstream unreachable: with no
 * persona, the article wizard cannot proceed at all.
 */
import { confirmDialog } from '../components/AppDialog.tsx';
import { useId, useState } from "react";
import {
  useLoaderData,
  useOutletContext,
  useRevalidator,
  useSearchParams,
} from "react-router";
import { del, imageUrl, patch, post, type Session } from "../api.ts";
import {
  Card,
  Empty,
  Pill,
  StatusPill,
  RowMenu,
  Table,
  jaNumber,
  rowOpen,
} from "../components/ui.tsx";
import { Field } from "../components/EditPanel.tsx";
import { ModalForm } from "../components/Modal.tsx";
import { AddFromLink } from "../components/AddFromLink.tsx";
import { inScope, sessionScope, type FactPanelProps } from '../fact-panel.ts';
import { ImageUpload } from "../components/ImageUpload.tsx";
import { Select } from "../components/Select.tsx";
import { Textarea } from "../components/Textarea.tsx";
import { AnimatePresence, listItem, m } from "../components/motion.tsx";
import { SkeletonRows, usePendingScreen } from "../components/Skeleton.tsx";

interface CatalogRow {
  id: string;
  status: "DRAFT" | "APPROVED" | "RETIRED";
  storeId: string | null;
  storeName?: string | null;
  /*
   * Provenance, present only on a row the harvester wrote (migration 062).
   *
   * Typed explicitly because the index signature below would otherwise make
   * them `unknown`, which cannot be rendered - and rendering them beside the
   * value is the whole point of storing them.
   */
  sourceUrl?: string | null;
  sourceQuote?: string | null;
  [field: string]: unknown;
}

/** How one field is entered. `select` carries its own options. */
type FieldKind =
  | "text"
  | "multiline"
  | "number"
  | "url"
  | "select"
  | "boolean"
  /** A fixed vocabulary, several of which may be chosen. Stored as an array. */
  | "chips";

interface FieldSpec {
  key: string;
  label: string;
  kind: FieldKind;
  required?: boolean;
  hint?: string;
  max?: number;
  options?: Array<{ value: string; label: string }>;
  /**
   * A `select` whose options are numbers.
   *
   * 優先度 is one: the column is an integer and the schema types it as such, so
   * the option's value has to be sent as a number rather than as the string the
   * control holds - and a stored value that matches no option has to land on the
   * nearest one instead of showing the control as unset.
   */
  numeric?: boolean;
  /**
   * What a NEW row starts on.
   *
   * A select with no value shows 「選択してください」, which is the right prompt for
   * a choice with no obvious answer and the wrong one for 種別: nearly every row
   * is a 施術, the column defaults to it, and making somebody state the common
   * case is a step that exists only because the control had no opinion.
   */
  defaultValue?: string;
  /**
   * How much of the form row this field takes.
   *
   * The panel auto-fits its columns, which is right for a row of short inputs
   * and wrong for a paragraph: a 2000-character body sharing a row with a
   * one-line title got a box the size of the title's.
   */
  span?: "full" | "wide";
  /**
   * Whether this field is a question worth asking of THIS row.
   *
   * ダウンタイム and リスク・副作用 are properties of a procedure, not of a price:
   * a 月額プラン has neither, and asking anyway left two boxes on the form
   * permanently blank and made the row look half-filled. A field that already
   * holds text is shown regardless of the predicate - hiding a stored value is
   * how it gets silently dropped on the next save.
   */
  when?: (draft: Record<string, unknown>) => boolean;
  /** Starting height of a growing textarea, in lines. */
  rows?: number;
}

interface View {
  title: string;
  blurb: string;
  columns: Array<[field: string, label: string, numeric?: boolean]>;
  fields: FieldSpec[];
}

/*
 * What a clinic actually offers as a booking route. A fixed list rather than free
 * text: these end up in generated copy as instructions to the reader, and
 * 「LINE」 and 「ライン」 and 「LINE予約」 are the same channel written three ways.
 */
const CTA_CHANNELS = [
  { value: "web", label: "Web予約" },
  { value: "line", label: "LINE" },
  { value: "phone", label: "電話" },
  { value: "mail", label: "メール" },
  { value: "instagram", label: "Instagram" },
  { value: "walkin", label: "来院・当日受付" },
];

const CATEGORY_OPTIONS = [
  { value: "STRENGTH", label: "強み" },
  { value: "TREATMENT_POLICY", label: "施術方針" },
  { value: "COUNSELING_POLICY", label: "カウンセリング方針" },
  { value: "EQUIPMENT", label: "設備" },
  /*
   * Not a kind of thing but the absence of one: material the clinic wants an
   * article to be able to use and that none of the four describes - aftercare
   * wording, a parking note, how a cartridge depth is chosen. Last, because
   * somebody looking for where equipment goes should find 設備 first and land
   * here only when nothing else fits.
   */
  { value: "OTHER", label: "その他" },
];

/**
 * What a row of 施術メニュー is.
 *
 * The screen was built for one thing a clinic sells - a procedure with a price,
 * a downtime and a risk profile - and a price page is not that: it carries
 * 5回コース, 月額プラン, モニター価格, カウンセリング料. Storing those was never the
 * problem; nothing said what a row WAS, so the form asked a monthly plan for its
 * downtime and the article described a course as a treatment.
 */
const KIND_OPTIONS = [
  { value: "TREATMENT", label: "施術" },
  { value: "COURSE", label: "回数コース" },
  { value: "PLAN", label: "月額プラン" },
  { value: "OFFER", label: "特別料金・モニター" },
  { value: "FEE", label: "カウンセリング料・各種費用" },
  { value: "OTHER", label: "その他" },
];

/** ダウンタイム・リスクは施術と回数コースにだけある質問。 */
const IS_PROCEDURE = (draft: Record<string, unknown>) =>
  draft.kind === "TREATMENT" || draft.kind === "COURSE";

const VIEWS: Record<string, View> = {
  treatments: {
    title: "施術・コース・料金",
    blurb:
      "クリニックが提供している有料メニューです。施術のほか、回数コース・月額プラン・" +
      "モニター価格・カウンセリング料も登録できます。価格やダウンタイムは、ここに書かれた値が" +
      "そのまま記事に引用されます。AIが数値を作ることはありません。",
    columns: [
      ["kind", "種別"],
      ["name", "名称"],
      ["priceJpy", "価格", true],
      ["priceNote", "価格の注記"],
    ],
    /*
     * Laid out the way the fields are used rather than the way the columns are
     * ordered: what it is, what it costs, what it involves. Risks and downtime
     * get full-width boxes because they are the two things a generated article
     * has to be accurate about, and a corner of a row invites one line.
     */
    fields: [
      {
        /*
         * First, because it decides which of the fields below are questions
         * worth answering: a 月額プラン has no downtime and no risk profile, and
         * asking for them made two of the five boxes on this form permanently
         * blank.
         */
        key: "kind",
        label: "種別",
        kind: "select",
        options: KIND_OPTIONS,
        defaultValue: "TREATMENT",
        // Short: the field sits in a narrow column, and a two-line hint here
        // ran under the neighbouring field's own hint.
        hint: "記事での書かれ方が変わります。",
      },
      {
        key: "name",
        label: "名称",
        kind: "text",
        required: true,
        max: 120,
        span: "wide",
        hint: "例：ハイフ（全顔）／ダーマペン5回コース／美肌月額プラン",
      },
      { key: "priceJpy", label: "価格（円）", kind: "number" },
      {
        key: "priceNote",
        label: "価格の注記",
        kind: "text",
        max: 200,
        span: "wide",
        hint: "例：1回・税込／初回限定。条件を書かなければ、記事も条件なしで書きます。",
      },
      {
        key: "description",
        label: "内容の説明",
        kind: "multiline",
        max: 4000,
        span: "full",
        rows: 5,
        hint:
          "何をするものか、どんな人に向くか。コースなら回数と有効期限、プランなら含まれる" +
          "内容まで。具体的なほど記事が一般論になりません。",
      },
      {
        key: "downtimeNote",
        when: IS_PROCEDURE,
        label: "ダウンタイム",
        kind: "multiline",
        max: 1000,
        span: "full",
        rows: 3,
        hint: "期間と、その間の過ごし方の注意。記事にそのまま引用されます。",
      },
      {
        key: "risks",
        when: IS_PROCEDURE,
        label: "リスク・副作用",
        kind: "multiline",
        max: 4000,
        span: "full",
        rows: 4,
        hint: "記載がなければ記事にも書かれません。広告としての適正さに直接影響します。",
      },
      {
        key: "devices",
        label: "使用機器・薬剤",
        kind: "text",
        max: 500,
        span: "wide",
        hint: "機器名や薬剤名。書かなければ記事も触れません（AIが創作することはありません）。",
      },
      {
        key: "detailUrl",
        label: "詳細ページ",
        kind: "text",
        max: 2000,
        span: "wide",
        hint: "https から始まるこの施術のページ。",
      },
    ],
  },
  personas: {
    title: "ペルソナ",
    blurb: "想定読者の属性と悩み。記事のトーンと構成の出発点になります。",
    columns: [
      ["label", "ラベル"],
      ["ageRange", "年代"],
      ["gender", "性別"],
      ["concerns", "悩み"],
    ],
    fields: [
      { key: "label", label: "ラベル", kind: "text", required: true, max: 100 },
      { key: "ageRange", label: "年代", kind: "text", max: 40 },
      { key: "gender", label: "性別", kind: "text", max: 40 },
      {
        key: "concerns",
        label: "悩み",
        kind: "multiline",
        max: 2000,
        span: "full",
        rows: 5,
      },
      { key: "tone", label: "希望トーン", kind: "text", max: 200 },
      {
        key: "notes",
        label: "備考",
        kind: "multiline",
        max: 2000,
        span: "full",
        rows: 3,
      },
      { key: "isDefault", label: "既定のペルソナ", kind: "boolean" },
    ],
  },
  supervisors: {
    title: "監修医",
    blurb:
      "記事内に挿入できる監修パーツの元データです。経歴は事実のみを登録してください。",
    columns: [
      ["photoId", "写真"],
      ["name", "氏名"],
      ["title", "肩書"],
      ["careerYears", "経験年数", true],
      ["qualifications", "資格"],
    ],
    fields: [
      {
        key: "name",
        label: "氏名",
        kind: "text",
        required: true,
        max: 100,
        span: "wide",
      },
      { key: "careerYears", label: "経験年数", kind: "number" },
      { key: "title", label: "肩書", kind: "text", max: 100, span: "wide" },
      {
        key: "qualifications",
        label: "資格・所属学会",
        kind: "multiline",
        max: 1000,
        span: "full",
        rows: 3,
        hint: "記事の監修表記に引用されます。学会名や認定名は正式名称で。",
      },
      {
        key: "profile",
        label: "プロフィール",
        kind: "multiline",
        max: 2000,
        span: "full",
        rows: 5,
        hint: "専門分野や診療の考え方。どんな医師かが分かるほど、記事の語り口がこの医院のものになります。",
      },
    ],
  },
  "cta-templates": {
    title: "CTAテンプレート",
    blurb:
      "予約導線のパーツ。リンクは https のみ受け付け、サーバー側で検証します。",
    columns: [
      ["name", "名称"],
      ["heading", "見出し"],
      ["buttonLabel", "ボタン文言"],
      ["buttonUrl", "リンク先"],
      ["channels", "予約手段"],
    ],
    fields: [
      {
        key: "name",
        label: "名称",
        kind: "text",
        required: true,
        max: 120,
        span: "wide",
        hint: "管理用の名前です。記事には出ません。",
      },
      { key: "buttonLabel", label: "ボタン文言", kind: "text", max: 60 },
      { key: "heading", label: "見出し", kind: "text", max: 200, span: "full" },
      {
        key: "body",
        label: "本文",
        kind: "multiline",
        max: 1000,
        span: "full",
        rows: 4,
        hint: "記事末尾に置かれる案内文。予約の流れや所要時間があると具体的になります。",
      },
      {
        key: "buttonUrl",
        label: "リンク先",
        kind: "text",
        max: 2000,
        span: "wide",
      },
      {
        /*
         * Writable through the API since the table was created, offered by no
         * screen, and dropped by the prompt renderer because a jsonb array is
         * not a scalar - so a clinic could not say "this CTA is for LINE" and
         * the model was never told either way.
         */
        key: "channels",
        label: "予約手段",
        kind: "chips",
        span: "full",
        options: CTA_CHANNELS,
        hint: "記事のCTAで案内する導線です。選んだものだけが生成に渡されます。",
      },
    ],
  },
  strengths: {
    title: "強み・方針・設備",
    blurb:
      "記事の導入や締めで引用される内容です。強み・施術方針・カウンセリング方針・設備を種別で分けて登録します。",
    columns: [
      ["category", "種別"],
      ["title", "タイトル"],
      ["body", "本文"],
    ],
    /*
     * The body decides whether an article reads as this clinic's or as any
     * clinic's, so it gets the room. It was sharing a row with the title and the
     * category and got a box three characters wide.
     */
    fields: [
      {
        key: "category",
        label: "種別",
        kind: "select",
        required: true,
        options: CATEGORY_OPTIONS,
      },
      {
        key: "title",
        label: "タイトル",
        kind: "text",
        required: true,
        max: 120,
        span: "wide",
        hint: "記事の見出しに使われることがあります。例：完全個室のカウンセリングルーム",
      },
      {
        key: "body",
        label: "本文",
        kind: "multiline",
        max: 4000,
        span: "full",
        rows: 6,
        hint: "そのまま引用できる具体さで。数値・体制・実際の運用など、他院と違う点があるほど記事が具体的になります。「その他」はページから貼り付けたままでもかまいません。",
      },
      {
        /*
         * A priority, not a sort index.
         *
         * The field is `sort_order` and the control said 「並び順 0」 with
         * 「小さいほど先に引用されます」 under it - which asks somebody registering a
         * clinic's strengths to think in list positions, and to keep a numbering
         * scheme in their head across a page of them. Nobody has ever wanted to
         * put a fact at position 7; they want to say this one matters most.
         *
         * Three bands, stored in the same column, spaced so a future band fits
         * between any two without renumbering anything.
         */
        key: "sortOrder",
        label: "優先度",
        kind: "select",
        numeric: true,
        options: [
          { value: "10", label: "高：まず引用してほしい" },
          { value: "50", label: "標準" },
          { value: "90", label: "低：ほかがないときだけ" },
        ],
        hint: "同じ優先度のものは登録順に引用されます。",
      },
    ],
  },
};

/*
 * Every stored vocabulary, in one map, because `formatCell` renders a cell
 * without knowing which column it came from. Two enums cannot collide here -
 * one is 強み's category and the other 施術メニュー's kind, and no value appears
 * in both except OTHER, which means the same thing in each.
 */
const CATEGORY_LABEL: Record<string, string> = Object.fromEntries(
  [...CATEGORY_OPTIONS, ...KIND_OPTIONS].map((option) => [option.value, option.label]),
);

/** Facts are scalars; anything else is a data bug, not something to render raw. */
function formatCell(value: unknown, numeric?: boolean): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "string" && CATEGORY_LABEL[value])
    return CATEGORY_LABEL[value];
  if (typeof value === "number")
    return numeric ? jaNumber(value) : String(value);
  if (typeof value === "string" || typeof value === "boolean")
    return String(value);
  // A chip list reads as a list, not as JSON.
  if (Array.isArray(value)) {
    const entries = value.filter((entry): entry is string => typeof entry === "string");
    return entries.length > 0 ? entries.join("・") : "—";
  }
  return "—";
}

/** The form's working copy. Strings throughout; converted on submit. */
type Draft = Record<string, string | boolean | string[]>;

function draftFrom(view: View, row: CatalogRow | null): Draft {
  const draft: Draft = {};
  for (const field of view.fields) {
    const value = row?.[field.key];
    if (field.kind === "boolean") {
      draft[field.key] = value === true;
      continue;
    }
    if (field.kind === "chips") {
      // A jsonb array arrives as a real array; anything else is a row written
      // before the column existed.
      draft[field.key] = Array.isArray(value)
        ? value.filter((entry): entry is string => typeof entry === "string")
        : [];
      continue;
    }
    /*
     * A numeric select lands on the nearest option it has.
     *
     * 優先度 replaced a free integer, and rows written before it hold anything
     * from 0 to 9999 - values no option carries, which would show the control as
     * unset and quietly rewrite the number on the next save. Snapped instead, so
     * an existing row opens on the band it is actually in.
     */
    if (row === null && field.defaultValue !== undefined) {
      draft[field.key] = field.defaultValue;
      continue;
    }
    if (field.numeric && field.options) {
      const current = typeof value === "number" ? value : Number(value ?? NaN);
      const nearest = Number.isFinite(current)
        ? field.options.reduce((best, option) =>
            Math.abs(Number(option.value) - current) < Math.abs(Number(best.value) - current)
              ? option
              : best,
          )
        : undefined;
      draft[field.key] = nearest?.value ?? field.options[1]?.value ?? "";
      continue;
    }
    // Scalars only. A jsonb column would otherwise land in the form as
    // "[object Object]" and be saved back as that literal string.
    draft[field.key] =
      typeof value === "string" || typeof value === "number"
        ? String(value)
        : "";
  }
  return draft;
}

/**
 * Only what the person actually filled in.
 *
 * An empty optional field is omitted rather than sent as '': the API treats an
 * absent field as "leave alone" and would reject '' for a URL that must match
 * https. A number is sent as a number, because the schema is typed.
 */
function payloadFrom(view: View, draft: Draft): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  for (const field of view.fields) {
    const value = draft[field.key];
    if (field.kind === "boolean") {
      payload[field.key] = value === true;
      continue;
    }
    if (field.kind === "chips") {
      // Sent even when empty, unlike the text fields: an emptied list is a CTA
      // whose channels were deliberately removed, and omitting it would leave
      // the old ones in place.
      payload[field.key] = Array.isArray(value) ? value : [];
      continue;
    }
    const text = String(value ?? "").trim();
    /*
     * A link emptied on purpose is sent as null - "no page" - rather than
     * omitted, which would leave the old link in place with no way to remove
     * it. Other text fields keep the omit rule above.
     */
    if (text === "" && field.key === "detailUrl") {
      payload[field.key] = null;
      continue;
    }
    if (text === "") continue;
    /*
     * A select whose options are numbers is still a number to the API. 優先度 is
     * the one: the column is `sort_order` and the schema types it as an integer,
     * so sending the option's value as a string is a 400.
     */
    payload[field.key] =
      field.kind === "number" || field.numeric ? Number(text) : text;
  }
  return payload;
}

export default function Catalog() {
  const data = useLoaderData<{ items: CatalogRow[]; resource: string }>();
  const session = useOutletContext<Session>();
  return (
    <CatalogPanel
      items={data.items}
      resource={data.resource}
      base=""
      scope={sessionScope(session)}
    />
  );
}

/**
 * The screen itself, on the clinic's route and on the JMC clinic page alike.
 * See fact-panel.ts for the three things that differ between the two.
 */
export function CatalogPanel({
  items: rows,
  resource,
  base,
  scope,
  clinicId,
  lead,
}: FactPanelProps<CatalogRow> & { resource: string }) {
  const pending = usePendingScreen();
  const view = VIEWS[resource] ?? {
    title: resource,
    blurb: "",
    columns: [["id", "ID"]] as View["columns"],
    fields: [],
  };

  /** null = closed, 'new' = creating, otherwise the row being edited. */
  const { isClinicAdmin, stores: ownStores, activeStore } = scope;

  /*
   * Which scopes the list shows.
   *
   * In the URL, so the view is shareable and the back button undoes it. The
   * default follows the store selector in the status strip: with a store chosen
   * the honest default is that store's *effective* set - the clinic's shared
   * rows plus its own - because that is what its articles are built from, and a
   * list showing every other store's rows alongside was showing material this
   * store cannot use.
   */
  const [params, setParams] = useSearchParams();
  const viewScope = params.get("scope") ?? (activeStore ? "effective" : "all");
  const data = {
    resource,
    items: rows.filter((row) => inScope(row, viewScope, activeStore?.id)),
  };

  const setViewScope = (next: string) => {
    const query = new URLSearchParams(params);
    // The default is expressed by its absence, so the URL stays clean and a
    // shared link does not pin a default that later changes.
    if (next === (activeStore ? "effective" : "all")) query.delete("scope");
    else query.set("scope", next);
    setParams(query);
  };

  /*
   * "This store only" and "shared plus this store" need a store to be about.
   * Without one selected there are exactly two useful views, and offering four
   * with two of them inert is worse than offering two.
   */
  const scopeLabelId = useId();
  /*
   * Three views with a store selected, two without.
   *
   * The labels name what is included rather than describing it: "共通＋この店舗"
   * is the set this store's articles are built from, and reading the label is
   * meant to be enough. The 対象 column in the table then labels every row with
   * the same vocabulary.
   */
  const scopeTabs = activeStore
    ? [
        { id: "effective", label: `共通 ＋ ${activeStore.name}` },
        { id: "clinic", label: "クリニック共通" },
        { id: "store", label: activeStore.name },
      ]
    : [
        { id: "all", label: "共通 ＋ 全店舗" },
        { id: "clinic", label: "クリニック共通" },
      ];

  const [editing, setEditing] = useState<"new" | CatalogRow | null>(null);
  /*
   * Which scope a new fact belongs to. '' is clinic-wide.
   *
   * A clinic admin chooses: shared material every store draws on, or something
   * for one branch. A store user has no choice to make - the server refuses a
   * clinic-wide write from them - so they get their own store and are told so
   * rather than shown a control that would fail.
   */
  const [newScope, setNewScope] = useState<string>(
    isClinicAdmin ? "" : (ownStores[0]?.id ?? ""),
  );
  // A photograph is saved the moment it is chosen, not with the form, so the
  // list behind the panel has to be told to re-read.
  const revalidate = useRevalidator();
  const [draft, setDraft] = useState<Draft>({});

  function open(target: "new" | CatalogRow) {
    setDraft(draftFrom(view, target === "new" ? null : target));
    /*
     * The scope selector starts on what this row actually IS, so opening an
     * edit and saving without touching it cannot move the fact. A clinic-wide
     * row is '' - the same empty value that means clinic-wide on create.
     */
    if (target === "new") setNewScope(isClinicAdmin ? "" : (ownStores[0]?.id ?? ""));
    else setNewScope(target.storeId ?? "");
    setEditing(target);
  }

  /**
   * Add or remove one chip.
   *
   * Reads the list inside the updater rather than from the render closure. Two
   * clicks in the same tick - a double click, or a keyboard repeat - both saw
   * the pre-click array otherwise, and the second one overwrote the first: the
   * chip visibly toggled and then un-toggled itself.
   */
  const toggleChip = (key: string, option: string) =>
    setDraft((current) => {
      // `Draft` values are string | boolean | string[], so narrowing by
      // Array.isArray is enough - no assertion needed.
      const value = current[key];
      const chosen = Array.isArray(value) ? value : [];
      return {
        ...current,
        [key]: chosen.includes(option)
          ? chosen.filter((entry) => entry !== option)
          : [...chosen, option],
      };
    });

  const set = (key: string, value: string | boolean | string[]) =>
    setDraft((current) => ({ ...current, [key]: value }));

  async function submit() {
    const payload = payloadFrom(view, draft);
    if (editing === "new") {
      // Absent means clinic-wide; the server reads that as store_id NULL and
      // refuses it from anybody who is not a clinic admin.
      await post(
        `${base}/${data.resource}`,
        newScope ? { ...payload, storeId: newScope } : payload,
      );
    } else if (editing) {
      /*
       * The scope goes with the edit, and only when it changed: sending it
       * every time would make a clinic admin's every save a tenancy write, and
       * the server refuses the field outright for anybody else - so an
       * unchanged value must not be sent by a store user at all.
       */
      const moved = (editing.storeId ?? "") !== newScope;
      await patch(`${base}/${data.resource}/${editing.id}`, {
        ...payload,
        ...(moved && isClinicAdmin ? { storeId: newScope === "" ? null : newScope } : {}),
      });
    }
  }

  /** Status is its own action: approving a fact is a decision, not a text edit. */
  async function setStatus(row: CatalogRow, status: string) {
    await patch(`${base}/${data.resource}/${row.id}`, { status });
  }

  /*
   * Removal, for a row that should not exist at all - most often a draft a
   * crawl read wrong. 廃止 used to sit beside this and meant "keep the row but
   * stop offering it", which is what 下書きに戻す already does; see the note on
   * the row's menu.
   */
  async function remove(row: CatalogRow) {
    await del(`${base}/${data.resource}/${row.id}/permanent`);
    await revalidate.revalidate();
  }

  return (
    <Card
      lead={lead}
      title={view.title}
      caption={`${view.blurb} 行を選ぶと全文が開き、その場で編集できます。`}
      actions={
        <>
          {/*
            Link first, manual under it. Somebody with a website should point at
            it; typing stays available and unchanged for everything a page does
            not say.
          */}
          <AddFromLink
            resource={data.resource}
            storeId={newScope || undefined}
            {...(clinicId ? { clinicId } : {})}
            onQueued={() => void revalidate.revalidate()}
          />
          <button
            className="btn btn--ghost btn--sm"
            type="button"
            onClick={() => open("new")}
          >
            ＋ 手動で追加
          </button>
        </>
      }
    >
      <p className="notice" style={{ marginBottom: 12 }}>
        AIが記事に書けるのは、ここで<strong>承認済み</strong>
        になっている事実だけです。未登録の価格・医師名・機器名が生成されることはありません。
      </p>

      {/*
        The form is a dialog, not a panel that unfolds where the button was.

        Adding a fact is a detour from a list somebody is reading, and an inline
        panel pushed the whole table down the page - so pressing 追加 moved the
        rows you were looking at and left the new fields wherever the button
        happened to be. A dialog keeps the list still, takes the focus, and
        returns it to the button on close, which is what every other add on this
        product already does (see Modal.tsx).
      */}
      <ModalForm
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={editing === "new" ? `${view.title}を追加` : `${view.title}を編集`}
        caption={
          editing === "new"
            ? "登録した内容は下書きです。承認するまで記事には使われません。"
            : "保存しても状態は変わりません。承認は一覧の操作メニューから行います。"
        }
        wide
        onSubmit={submit}
        submitLabel={editing === "new" ? "登録する" : "保存する"}
      >
        {/*
          The scope, on both create AND edit.

          It used to be create-only, on the reasoning that moving a fact between
          stores is a tenancy change rather than a field edit. True, and not a
          reason to make it impossible: a fact registered against one store and
          later wanted everywhere had to be deleted and retyped, which throws
          away its provenance and its approval. The server allows the move for a
          clinic admin only - see the note on the PATCH route.
        */}
        {isClinicAdmin ? (
          <Field
              label="登録先"
              htmlFor="c-scope"
              span="full"
              hint={
                editing === "new"
                  ? "クリニック共通にすると、すべての店舗の記事で使えます。店舗を選ぶと、その店舗だけの情報になります。"
                  : "変更すると、この情報を引用できる店舗が変わります。公開済みの記事の文面は変わりません。"
              }
            >
              <Select
                id="c-scope"
                value={newScope}
                onChange={setNewScope}
                options={[
                  { value: "", label: "クリニック共通（全店舗で使用）" },
                  ...ownStores.map((store) => ({
                    value: store.id,
                    label: `${store.name}のみ`,
                  })),
                ]}
              />
            </Field>
        ) : (
          <p className="notice" role="status">
            登録先：
            <strong>
              {ownStores.find((store) => store.id === newScope)?.name ??
                "担当店舗"}
            </strong>
            の情報です。クリニック共通の情報はクリニック管理者が扱います。
          </p>
        )}

        {view.fields
          .filter(
            (field) =>
              !field.when ||
              field.when(draft) ||
              String(draft[field.key] ?? "") !== "",
          )
          .map((field) => (
          <Field
            key={field.key}
            label={field.label}
            htmlFor={`f-${field.key}`}
            {...(field.span ? { span: field.span } : {})}
            {...(field.required ? { required: true } : {})}
            {...(field.hint ? { hint: field.hint } : {})}
          >
            {field.kind === "multiline" ? (
              <Textarea
                id={`f-${field.key}`}
                minRows={field.rows ?? 3}
                maxLength={field.max}
                value={String(draft[field.key] ?? "")}
                onChange={(e) => set(field.key, e.target.value)}
              />
            ) : field.kind === "select" ? (
              <Select
                id={`f-${field.key}`}
                value={String(draft[field.key] ?? "")}
                onChange={(value) => set(field.key, value)}
                options={field.options ?? []}
              />
            ) : field.kind === "chips" ? (
              /*
               * Toggle buttons rather than a multi-select: the vocabulary is six
               * items long, and `aria-pressed` says what is chosen without a
               * listbox the reader has to open to find out.
               */
              <div className="chips" id={`f-${field.key}`} role="group" aria-label={field.label}>
                {(field.options ?? []).map((option) => {
                  const chosen = Array.isArray(draft[field.key])
                    ? (draft[field.key] as string[])
                    : [];
                  return (
                    <button
                      key={option.value}
                      type="button"
                      className="chip"
                      aria-pressed={chosen.includes(option.value)}
                      onClick={() => toggleChip(field.key, option.value)}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            ) : field.kind === "boolean" ? (
              <label className="checkline">
                <input
                  id={`f-${field.key}`}
                  type="checkbox"
                  checked={draft[field.key] === true}
                  onChange={(e) => set(field.key, e.target.checked)}
                />
                はい
              </label>
            ) : (
              <input
                id={`f-${field.key}`}
                type={
                  field.kind === "number"
                    ? "number"
                    : field.kind === "url"
                      ? "url"
                      : "text"
                }
                {...(field.required ? { required: true } : {})}
                {...(field.max ? { maxLength: field.max } : {})}
                value={String(draft[field.key] ?? "")}
                onChange={(e) => set(field.key, e.target.value)}
              />
            )}
          </Field>
        ))}
        {/*
          Only for a row that exists: the photograph is uploaded against an id,
          and a row being created has none yet. One save, then the picture.
        */}
        {data.resource === "supervisors" &&
          editing !== "new" &&
          editing !== null && (
            <Field
              label="顔写真"
              htmlFor="sup-photo"
              hint="記事の監修パーツに表示されます。保存ボタンとは別に、選んだ時点で反映されます。"
            >
              <ImageUpload
                imageId={
                  typeof editing.photoId === "string" ? editing.photoId : null
                }
                endpoint={`${base}/supervisors/${editing.id}/photo`}
                label="写真を選ぶ"
                placeholder={
                  typeof editing.name === "string" && editing.name.trim()
                    ? editing.name.trim().charAt(0)
                    : "医"
                }
                onDone={() => revalidate.revalidate()}
              />
            </Field>
          )}
      </ModalForm>

      {/*
          A filter, labelled as one.

          It was a tab strip with a paragraph of explanation beside it, and a tab
          strip says "these are sections of this page" - so it read as two
          subsections of 強み rather than as two views of one list. The standard
          shape for "which subset am I looking at" is a labelled segmented
          control with the size of each subset on it: the count is what tells
          somebody what a segment contains, and it does it without a sentence.

          Labelled 表示 - what is shown - and not 対象. 対象 is what a row *is*:
          the table column reports it and the field in the edit panel sets it.
          Using the same word for the filter put two controls with one label on
          top of each other, one changing what is listed and the other changing
          what a new record applies to. The segment labels still name the scopes,
          so the connection to the column is not lost.

          It sits against the table rather than above the panel, because that is
          what it filters.
        */}
      {(isClinicAdmin || activeStore) && (
        <div className="scopebar">
          <span className="scopebar__label" id={scopeLabelId}>
            表示
          </span>
          <div
            className="scopebar__options"
            role="group"
            aria-labelledby={scopeLabelId}
          >
            {scopeTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className="scopeopt"
                aria-pressed={tab.id === viewScope}
                onClick={() => setViewScope(tab.id)}
              >
                {tab.label}
                {/*
                    Counted from the rows on screen for the current view, and from
                    nothing for the others - a count per segment would need a
                    query per segment. So only the selected one carries a number,
                    which is the honest version: it says how much you are looking
                    at, not how much you would be.
                  */}
                {tab.id === viewScope && (
                  <span className="scopeopt__count">
                    {jaNumber(data.items.length)}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {pending ? (
        // Only while there is nothing to show: a background refresh keeps the
        // rows on screen. See Skeleton.tsx.
        <SkeletonRows rows={5} columns={view.columns.length + 2} />
      ) : data.items.length === 0 ? (
        <Empty>まだ登録がありません。「＋ 新規登録」から追加します。</Empty>
      ) : (
        <Table
          head={
            <>
              {view.columns.map(([, label, numeric]) => (
                <th
                  key={label}
                  scope="col"
                  className={numeric ? "num" : undefined}
                >
                  {label}
                </th>
              ))}
              <th scope="col">対象</th>
              <th scope="col">状態</th>
              <th scope="col">操作</th>
            </>
          }
        >
          {/*
            Rows arrive, leave and move rather than cutting.
            
            These lists change while somebody is looking at them - approving a
            row drops it out of a filtered view, adding one puts it in the middle
            of the sort order - and without this the change is a jump cut that
            says nothing about what happened. See motion.tsx.
          */}
          {/*
            Default mode, not popLayout: popLayout lifts a leaving item out with
            position:absolute, and a table row taken out of its table loses its
            column widths and squashes. Here it fades where it is, then the rows
            below glide up (listItem's layout spring).
          */}
          <AnimatePresence>
            {data.items.map((row, index) => (
              // The row opens the edit; the buttons in it still do their own job.
              <m.tr
                key={row.id}
                data-motion
                layout
                {...listItem(index)}
                {...rowOpen(() => open(row))}
              >
              {view.columns.map(([field, , numeric]) =>
                field === "photoId" ? (
                  <td key={field}>
                    {typeof row.photoId === "string" ? (
                      // Immutable URL, so this is one request per face for the
                      // life of the picture no matter how often the list loads.
                      <img
                        className="facephoto"
                        src={imageUrl(row.photoId)}
                        alt=""
                      />
                    ) : (
                      <span className="muted small">なし</span>
                    )}
                  </td>
                ) : (
                  <td key={field} className={numeric ? "num" : undefined}>
                    {/*
                      A long body is clamped to two lines, with the row still the
                      way to read it.

                      その他 rows are whole passages lifted off a clinic's page,
                      and a 4,000-character one made its row taller than the rest
                      of the table put together - so a list of five facts became
                      a page of scrolling and the short rows above and below it
                      were pushed out of view. Clamped rather than cut in the
                      string, because the ellipsis then falls where the column
                      actually runs out at this width rather than at a character
                      count guessed here.
                    */}
                    {view.fields.find((spec) => spec.key === field)?.kind === "multiline" ? (
                      <span className="cellclamp">{formatCell(row[field], numeric)}</span>
                    ) : (
                      formatCell(row[field], numeric)
                    )}
                  </td>
                ),
              )}
              <td>
                {/*
                  Which stores this reaches. The distinction matters more than it
                  looks: editing a clinic-wide fact changes what every store's
                  articles say, and the two are otherwise indistinguishable rows.
                */}
                {row.storeId === null ? (
                  <Pill>クリニック共通</Pill>
                ) : (
                  <Pill tone="muted">{row.storeName ?? "店舗"}</Pill>
                )}
              </td>
              <td>
                <StatusPill status={row.status} />
                {/*
                  Where a harvested value came from, beside the value itself.
                  This is what makes reviewing a draft a decision rather than an
                  errand: the price and the sentence it was read out of on one
                  screen. A typed row has no provenance and shows nothing.
                */}
                {row.sourceUrl && (
                  <details className="muted small" style={{ marginTop: 4 }}>
                    <summary>取得元</summary>
                    {row.sourceQuote && (
                      <p style={{ margin: '4px 0' }}>「{row.sourceQuote}」</p>
                    )}
                    <a href={row.sourceUrl} target="_blank" rel="noreferrer noopener">
                      {row.sourceUrl}
                    </a>
                  </details>
                )}
              </td>
              <td>
                {/*
                  Every action behind the menu, except approval.

                  Approval is the gate the whole product rests on - only an
                  APPROVED fact reaches a generation - so it stays a button on
                  the row where somebody reviewing a page of harvested drafts can
                  work down the list without opening anything. The rest are
                  occasional and were four buttons wide, which made the busiest
                  column on the screen the one nobody uses.

                  廃止 IS GONE. It set RETIRED, which meant "keep the row so an
                  article that cited it still resolves, but stop offering it" -
                  and 下書きに戻す already does exactly that: a DRAFT is kept,
                  resolvable and invisible to generation. Two controls for one
                  outcome, one of them a dead end (nothing on this screen brought
                  a RETIRED row back), beside a 削除 that really does remove it.
                  Rows already retired still say so and can be returned to draft.
                */}
                <div className="rowactions">
                  {row.status !== "APPROVED" && row.status !== "RETIRED" && (
                    <StatusButton
                      row={row}
                      status="APPROVED"
                      label="承認"
                      onDone={setStatus}
                    />
                  )}
                  {/*
                    No 編集 here: the row itself opens the editor, and a menu
                    entry that repeats the row's own click is one more thing to
                    read before finding 削除. The menu carries what the row
                    cannot do.
                  */}
                  <RowMenu>
                    {row.status === "APPROVED" && (
                      <button type="button" onClick={() => void setStatus(row, "DRAFT")}>
                        下書きに戻す
                      </button>
                    )}
                    {row.status === "RETIRED" && (
                      <button type="button" onClick={() => void setStatus(row, "DRAFT")}>
                        下書きに戻す
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() =>
                        void confirmDialog({
                          title: "この情報を削除しますか？",
                          message: "この情報を完全に削除します。元に戻せません。",
                          confirmLabel: "削除する",
                          danger: true,
                        }).then((ok) => (ok ? remove(row) : undefined))
                      }
                    >
                      完全に削除
                    </button>
                  </RowMenu>
                </div>
              </td>
              </m.tr>
            ))}
          </AnimatePresence>
        </Table>
      )}
    </Card>
  );
}

/**
 * A row action that writes, then refreshes the list from the server.
 *
 * Its own component because each row needs its own pending state - a shared one
 * would grey out every button on the screen while one of them worked.
 */
function StatusButton({
  row,
  status,
  label,
  onDone,
}: {
  row: CatalogRow;
  status: string;
  label: string;
  onDone: (row: CatalogRow, status: string) => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  return (
    <button
      className="btn btn--ghost btn--sm"
      type="button"
      disabled={busy}
      onClick={() => {
        setBusy(true);
        void onDone(row, status).finally(() => setBusy(false));
      }}
    >
      {busy ? "…" : label}
    </button>
  );
}
